import i18n from 'i18next';
import type { Locale } from 'date-fns';
import { enUS, ru, de, es, fr } from 'date-fns/locale';

/** Возвращает текущую локаль i18n или системную локаль браузера. */
export const getLocale = (): string => i18n.language || navigator.language || 'ru';

/** Returns the date-fns Locale object for the current i18n language. */
export const getDateFnsLocale = (): Locale => {
  const lang = (i18n.language || navigator.language || 'en').split('-')[0];
  switch (lang) {
    case 'ru': return ru;
    case 'de': return de;
    case 'es': return es;
    case 'fr': return fr;
    default: return enUS;
  }
};

export const formatDate = (date: string | Date): string => {
  const d = new Date(date);
  return d.toLocaleDateString(getLocale(), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const formatShortDate = (date: string | Date): string => {
  const d = new Date(date);
  return d.toLocaleDateString(getLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const formatTime = (date: string | Date): string => {
  const d = new Date(date);
  return d.toLocaleTimeString(getLocale(), {
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatDateTime = (date: string | Date): string => {
  const d = new Date(date);
  return d.toLocaleString(getLocale(), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Формат «янв. 2026» / «Jan 2026» — для подписей на графиках. */
export const formatMonthYear = (date: string | Date): string => {
  const d = new Date(date);
  return d.toLocaleDateString(getLocale(), {
    month: 'short',
    year: 'numeric',
  });
};

export const isOverdue = (date: string | Date): boolean => {
  return new Date(date) < new Date();
};

export const getDaysUntil = (date: string | Date): number => {
  const target = new Date(date);
  const today = new Date();
  const diffTime = target.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Normalises a date string (YYYY-MM-DD or DD.MM.YYYY) plus an optional
 * HH:mm time string into an ISO-8601 UTC string without milliseconds.
 * Falls back to the current time if the input cannot be parsed.
 *
 * AD-538: дата и время — местные часы браузера, то есть то, что человек ввёл
 * в поля. Раньше они читались как UTC, и событие сдвигалось на смещение пояса.
 * Сдвиг берётся по правилам пояса на ЭТУ дату (летнее время), а не на сегодня.
 */
export const toIsoUtc = (dateStr: string, timeStr?: string): string => {
  let y = 0, mo = 0, d = 0;

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const parts = dateStr.split('-').map(Number);
    y = parts[0] ?? 0; mo = parts[1] ?? 0; d = parts[2] ?? 0;
  } else if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
    const parts = dateStr.split('.').map(Number);
    y = parts[2] ?? 0; mo = parts[1] ?? 0; d = parts[0] ?? 0;
  } else {
    const fallback = new Date(dateStr);
    if (isNaN(fallback.getTime())) {
      return new Date().toISOString().replace('.000Z', 'Z');
    }
    return fallback.toISOString().replace('.000Z', 'Z');
  }

  let hh = 9, mi = 0;
  if (timeStr && /^\d{2}:\d{2}$/.test(timeStr)) {
    const timeParts = timeStr.split(':').map(Number);
    hh = timeParts[0] ?? 9;
    mi = timeParts[1] ?? 0;
  }

  return new Date(y, mo - 1, d, hh, mi, 0)
    .toISOString()
    .replace('.000Z', 'Z');
};
